/* eslint-disable max-len */
import React, { useState, forwardRef, useImperativeHandle, useCallback, useContext } from 'react';
import { useForm } from 'react-hook-form';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  Row,
  Col,
  Progress,
  Badge,
  ListGroup,
  ListGroupItem,
} from 'reactstrap';

import { yupResolver } from '@hookform/resolvers/yup';

import { AppContext } from '../../../contexts/app';
import { useManager } from '../../../hooks/manager';
import { useFetchData } from '../../../hooks/useFetchData';
import api from '../../../services/api';
import { objectToFieldValueArrayString, objectToFormattedString } from '../../../utils/functions';
import { schema } from './schemaValidations';

function CustomModalFecharFaturas(_props, ref) {
  const [show, setShow] = useState(false);
  const [data, setData] = useState({});
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [processingStatus, setProcessingStatus] = useState({});
  const [processedIds, setProcessedIds] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [type, setType] = useState('conta-pagar');

  const { loading, setLoading, notificationRef } = useContext(AppContext);
  const { setDatas } = useManager();
  const { handleSubmit } = useForm({ resolver: yupResolver(schema) });

  // Hook para buscar dados de cada linha
  const { fetchData: fetchLineData } = useFetchData({
    onSuccess: (responseData, response, contextData) => {
      const rowId = contextData?.rowId;
      if (rowId) {
        setProcessingStatus((prev) => ({
          ...prev,
          [rowId]: { status: 'completed', data: responseData, response },
        }));
      }
    },
    onError: (error, errorMessage, contextData) => {
      const rowId = contextData?.rowId;
      if (rowId) {
        setProcessingStatus((prev) => ({
          ...prev,
          [rowId]: { status: 'error', error: errorMessage },
        }));
      }
    },
  });

  useImperativeHandle(ref, () => ({
    show: (dataValues) => {
      setType(dataValues.type);
      setData(dataValues);
      // setOnReload(dataValues.onReload);
      setShow(true);
    },
    close: () => setShow(false),
  }));

  const onSuccess = useCallback((savedData) => {
    console.log('🚀 ~ file: CustomModalFecharFaturas index.js ~ onSuccess ~ savedData:', savedData);
    const { onSave } = data;
    if (typeof onSave === 'function') {
      onSave(savedData);
    }

    setShow(false);
  }, [data]);

  const processLineData = useCallback(async (selectedRow) => {
    // Cria um ID único para o objeto complexo usando objectToFormattedString
    const rowId = objectToFormattedString(selectedRow);
    const query = type === 'conta-pagar' ? `/financeiros-contas-pagar?search=${objectToFieldValueArrayString(selectedRow)}` : `/financeiros-contas-receber?search=${objectToFieldValueArrayString(selectedRow)}`;

    return new Promise((resolve, reject) => {
      fetchLineData(query, {
        contextData: { rowId }, // Passa o rowId como contextData
        onSuccess: (responseData, response) => {
          resolve({ rowId, status: 'completed', data: responseData, response });
        },
        onError: (error, errorMessage) => {
          reject(new Error(JSON.stringify({ rowId, status: 'error', error: errorMessage })));
        },
      }).catch(() => {
        setProcessingStatus((prev) => ({
          ...prev,
          [rowId]: { status: 'error', error: 'Erro ao processar linha' },
        }));
        reject(new Error(JSON.stringify({ rowId, status: 'error', error: 'Erro ao processar linha' })));
      });
    });
  }, [fetchLineData]);

  const onSubmit = useCallback(async (formValues) => {
    console.log('🚀 ~ onSubmit ~ formValues:', formValues);
    const { selectedRows } = data;

    if (!selectedRows || selectedRows.length === 0) {
      notificationRef.current.notify({
        message: 'Nenhuma linha selecionada',
        color: 'warning',
      });
      return;
    }

    // Inicializa o processamento
    setIsProcessing(true);
    setShowProgressModal(true);
    setProcessingStatus({});
    setProcessedIds([]);
    setShow(false); // Fecha o modal principal

    try {
      // Processa cada linha individualmente
      const processPromises = selectedRows.map((selectedRow) => {
        const rowId = objectToFormattedString(selectedRow);
        setProcessingStatus((prev) => ({
          ...prev,
          [rowId]: { status: 'processing', data: selectedRow },
        }));
        return processLineData(selectedRow);
      });

      // Aguarda o processamento de todas as linhas e captura os resultados
      const results = await Promise.allSettled(processPromises);

      console.log('🚀 ~ results:', results);
      console.log('🚀 ~ processedIds:', processedIds);

      // Verifica se todas as linhas foram processadas com sucesso
      const successfulResults = results.filter((result) => (
        result.status === 'fulfilled' && result.value?.status === 'completed'
      ));

      const allProcessed = successfulResults.length === selectedRows.length;

      // Constrói o array de IDs processados baseado nos resultados
      const processedIdsFromResults = successfulResults.map((result) => result.value.rowId);

      console.log('🚀 ~ allProcessed:', allProcessed);
      console.log('🚀 ~ successfulResults.length:', successfulResults);
      console.log('🚀 ~ selectedRows.length:', selectedRows.length);
      console.log('🚀 ~ processedIdsFromResults:', processedIdsFromResults);

      if (allProcessed && processedIdsFromResults.length > 0) {
        // Faz a requisição PUT com os IDs processados
        await Promise.all(successfulResults.map(async (row) => {
          try {
            const originalIds = row?.value?.data?.map((item) => item._id) || row?.value?.data?.map((item) => item._id).flat(); // Usa o _id se existir, senão usa o objeto inteiro
            console.log('🚀 ~ awaitPromise.all ~ originalIds:', originalIds);

            const { data: faturasFechadas } = await api.put('/financeiroFechamentos', {
              ids: originalIds,
            });

            // onSuccess(faturasFechadas);
            setProcessingStatus((prev) => ({
              ...prev,
              [row.value.rowId]: { status: 'completed', data: faturasFechadas },
            }));

            setIsProcessing(false);
          } catch (err) {
            setLoading(false);
            console.log('🚀 ~ file: CustomModalFecharFaturas index.js ~ err:', err);

            if (err && err.response && err.response.data) {
              const { message } = err.response.data;
              if (message) {
                notificationRef.current.notify({
                  message,
                  color: 'danger',
                });
                return;
              }
            }

            notificationRef.current.notify({
              message: 'Falha para fechar faturas',
              color: 'danger',
            });
          }
        }));

        console.log('🚀 ~ awaitPromise.all ~ data.onReload:', data.onReload);
        if (typeof data.onReload === 'function') {
          data.onReload();
        }
      } else {
        const failedResults = results.filter((result) => (
          result.status === 'rejected' || (result.status === 'fulfilled' && result.value?.status === 'error')
        ));

        console.log('🚀 ~ failedResults:', failedResults);

        notificationRef.current.notify({
          message: `${failedResults.length} linha(s) não foram processadas com sucesso`,
          color: 'warning',
        });
      }
    } catch (error) {
      console.error('Erro no processamento:', error);
      notificationRef.current.notify({
        message: 'Erro no processamento das faturas',
        color: 'danger',
      });
    } finally {
      setIsProcessing(false);
    }
  }, [data, processLineData, processedIds, processingStatus, setDatas, onSuccess, notificationRef, setLoading, fetchLineData]);

  const { selectedRows = [], subTitle } = data;

  // Calcula o progresso
  const totalRows = selectedRows.length;
  const completedRows = Object.values(processingStatus).filter((status) => status.status === 'completed').length;
  const errorRows = Object.values(processingStatus).filter((status) => status.status === 'error').length;
  const progressPercentage = totalRows > 0 ? Math.round((completedRows / totalRows) * 100) : 0;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'processing':
        return <i className="fa-solid fa-spinner fa-spin text-primary mr-2" />;
      case 'completed':
        return <i className="fa-solid fa-check-circle text-success mr-2" />;
      case 'error':
        return <i className="fa-solid fa-exclamation-circle text-danger mr-2" />;
      default:
        return <i className="fa-solid fa-circle text-muted mr-2" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'processing':
        return <Badge color="primary">Processando</Badge>;
      case 'completed':
        return <Badge color="success">Concluído</Badge>;
      case 'error':
        return <Badge color="danger">Erro</Badge>;
      default:
        return <Badge color="secondary">Pendente</Badge>;
    }
  };

  return (
    <>
      <Modal
        isOpen={show}
        onClose={() => setShow(false)}
        centered
      >
        <Form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>
            <strong>Fechar Faturas</strong>
            <Button
              className="close position-absolute top-4 right-4 py-1 px-2"
              onClick={() => setShow(false)}
            >
              <i className="fas fa-times" />
            </Button>
          </ModalHeader>
          <ModalBody>
            <Row>
              <Col>
                <strong>{subTitle || `Deseja realmente fechar faturas da(s) ${selectedRows?.length} linha(s) selecionada(s)?`}</strong>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter tag="div" className="d-flex justify-content-end">
            <Button type="submit" color="success" disabled={loading?.loading || loading}>Fechar Fatura</Button>
            <Button color="danger" className="ml-3" onClick={() => setShow(false)} disabled={loading?.loading || loading}>Cancelar</Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Modal de Progresso */}
      <Modal
        isOpen={showProgressModal}
        onClose={() => !isProcessing && setShowProgressModal(false)}
        centered
        size="lg"
      >
        <ModalHeader>
          <strong>Fechando Faturas</strong>
          <Button
            className="close position-absolute top-4 right-4 py-1 px-2"
            onClick={() => !isProcessing && setShowProgressModal(false)}
            disabled={isProcessing}
          >
            <i className="fas fa-times" />
          </Button>
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col>
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-2">
                  <span>
                    Progresso:
                    {' '}
                    {completedRows}
                    /
                    {totalRows}
                    {' '}
                    faturas fechadas
                  </span>
                  <span>
                    {progressPercentage}
                    %
                  </span>
                </div>
                <Progress
                  value={progressPercentage}
                  color={errorRows > 0 ? 'warning' : 'success'}
                  className="mb-3"
                />
              </div>

              <div className="mb-3">
                <h6>Status do Processamento:</h6>
                <ListGroup>
                  {selectedRows.map((row, index) => {
                    const rowId = objectToFormattedString(row);
                    const status = processingStatus[rowId]?.status || 'pending';
                    return (
                      <ListGroupItem key={rowId} className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          {getStatusIcon(status)}
                          <span>
                            <strong>
                              Linha
                              {' '}
                              {index + 1}
                              :
                            </strong>
                            {' '}
                            {row.profissionalNome || row.grupoNome || row.clienteNome || 'N/A'}
                          </span>
                        </div>
                        {getStatusBadge(status)}
                      </ListGroupItem>
                    );
                  })}
                </ListGroup>
              </div>

              {errorRows > 0 && (
                <div className="alert alert-warning">
                  <i className="fa-solid fa-exclamation-triangle mr-2" />
                  {errorRows}
                  {' '}
                  linha(s) apresentaram erro durante o processamento.
                </div>
              )}

              {isProcessing && (
                <div className="text-center">
                  <i className="fa-solid fa-spinner fa-spin fa-2x text-primary" />
                  <p className="mt-2">Processando faturas...</p>
                </div>
              )}
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setShowProgressModal(false)}
            disabled={isProcessing}
          >
            {isProcessing ? 'Processando...' : 'Fechar'}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default forwardRef(CustomModalFecharFaturas);
